import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, ReplaySubject } from 'rxjs';

import { MODULE } from '@shared/consts';
import { NotificationService, UserService } from '@shared/services';
import { DialogClosed } from '@signal-store/models';
import { UserStore } from '@signal-store/user.store';

import { ModuleConfigDialogData, ModuleConfigDialogResult, ModuleConfigDialogTarget } from './common';
import type { ModuleConfigDialogComponent } from './module-config-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ModuleConfigDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly userService = inject(UserService);
  private readonly userStore = inject(UserStore);

  openModuleDefinition(target: ModuleConfigDialogTarget): Observable<ModuleConfigDialogResult> {
    return this.open(this.toData(target, false));
  }

  openModuleSettings(target: ModuleConfigDialogTarget): Observable<ModuleConfigDialogResult> {
    return this.open(this.toData(target, true), result => {
      if (result?.reloadGrid) {
        this.userStore.reloadMenu();
        this.userStore.closeJsDialog(DialogClosed.ModuleSettings);
      }
    });
  }

  /** A legacy page refreshes the menu itself (`triggerMenuRefresh`); its `isLoadingMenu` watcher reloads the page. */
  openLegacyModuleSettings(target: ModuleConfigDialogTarget): Observable<ModuleConfigDialogResult> {
    return this.open(this.toData(target, true));
  }

  private toData({ moduleId, name, iconId }: ModuleConfigDialogTarget, isConfig: boolean): ModuleConfigDialogData {
    return isConfig ? { moduleId, name, iconId, isConfig } : { moduleId, name, iconId };
  }

  private open(
    data: ModuleConfigDialogData,
    afterClosed?: (result: ModuleConfigDialogResult) => void,
  ): Observable<ModuleConfigDialogResult> {
    /* Hot: the page headers never subscribe, and the dialog still has to open for them. */
    const closed = new ReplaySubject<ModuleConfigDialogResult>(1);
    /* Lazy: the page header injects this on every page, and the tabs are large. */
    import('./module-config-dialog.component')
      .then(({ ModuleConfigDialogComponent: component }) => {
        const previousModule = this.pointHeaderAt(data.moduleId);
        this.dialog
          .open<ModuleConfigDialogComponent, ModuleConfigDialogData, ModuleConfigDialogResult>(component, {
            panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full', 'module-config-dialog'],
            ...this.userService.getResponsiveDialogSize().xl,
            data,
          })
          .afterClosed()
          .subscribe(result => {
            this.restoreHeader(previousModule);
            afterClosed?.(result);
            closed.next(result);
            closed.complete();
          });
      })
      .catch(() => {
        this.notification.error('errors.unidentifiedError');
        closed.complete();
      });
    return closed.asObservable();
  }

  /** Requests that do not name a module are scoped to the one being configured, as in the legacy dialog. */
  private pointHeaderAt(moduleId: number): string {
    const previous = sessionStorage.getItem(MODULE);
    if (moduleId) {
      sessionStorage.setItem(MODULE, moduleId.toString());
    } else {
      sessionStorage.removeItem(MODULE);
    }
    return previous;
  }

  private restoreHeader(previous: string) {
    if (previous === null) {
      sessionStorage.removeItem(MODULE);
    } else {
      sessionStorage.setItem(MODULE, previous);
    }
  }
}
